/**
 * PPM (Plant Performance Module) broker.
 *
 * Load PR results and trigger a billing-period PPM run.
 */
import AxiosManager from '@/utils/AxiosManager'
import Constants from '@/helpers/Constants'

export interface PpmSummary {
  sample_count: number
  valid_hours: number
  invalid_hours: number
  hour_count: number
  avg_pr: number | null
  // Phase 7 W-C — temperature-corrected PR (Schedule 17 §3.12).
  avg_apr?: number | null
  avg_ppr?: number | null
  avg_prd?: number | null
  tc_metrics_available?: boolean
}

export interface PpmHourlyRow {
  hour_start: string
  value_avg: number | null
  is_valid: boolean
  invalid_reason: string | null
  sample_count: number | null
  valid_sample_count: number | null
  // Phase 7 W-C — hourly actual / projected PR.
  apr?: number | null
  ppr?: number | null
}

export interface PpmSampleRow {
  ts_minute: string
  hour_start: string
  sample_value: number | null
  qf: number | null
}

export interface PpmResults {
  period: {
    id: number
    name: string
    status: string
    is_trial: boolean
    period_start: string | null
    period_end: string | null
  }
  summary: PpmSummary
  hourly: PpmHourlyRow[]
  samples: {
    total: number
    offset: number
    limit: number
    hour_start: string | null
    rows: PpmSampleRow[]
  }
}

export default class PpmBroker {
  private static get ax(): AxiosManager {
    return new AxiosManager(Constants.SERVER_URL)
  }

  public static async getBillingPeriods(): Promise<any[]> {
    // Period list is served by the PPM controller so ppm:read alone is enough.
    const res = await PpmBroker.ax.get('/get?comp=ppm')
    return res ?? []
  }

  public static async getResults(args: {
    billing_period_id: number
    hour_start?: string | null
    limit?: number
    offset?: number
  }): Promise<PpmResults> {
    let url = `/get?comp=ppm&billing_period_id=${args.billing_period_id}`
    if (args.hour_start) {
      url += `&hour_start=${encodeURIComponent(args.hour_start)}`
    }
    if (args.limit != null) {
      url += `&limit=${args.limit}`
    }
    if (args.offset != null) {
      url += `&offset=${args.offset}`
    }
    return await PpmBroker.ax.get(url)
  }

  public static async run(billing_period_id: number): Promise<{
    sample_count: number
    valid_hours: number
    invalid_hours: number
  }> {
    return await PpmBroker.ax.post('/run_ppm', { billing_period_id })
  }
}
